/**
 * Sprint 19 B1: AMP Email HTML Renderer
 *
 * Generates AMP4Email HTML document alongside standard HTML.
 * Gmail, Yahoo, and Mail.ru show AMP; all others get fallback.
 *
 * AMP4Email requirements:
 * - DOCTYPE: html ⚡4email
 * - Required: <script async src="https://cdn.ampproject.org/v0.js"></script>
 * - No external CSS (inline only, <style amp4email>)
 * - Component-specific scripts in <head>
 * - All XHR via amp-list/amp-form (CORS-enabled endpoints)
 */

import type { AmpComponentType } from "./amp-components.js";
import { AMP_COMPONENT_REGISTRY } from "./amp-components.js";

export interface AmpEmailComponent {
  type: AmpComponentType;
  props: Record<string, unknown>;
}

export interface AmpRenderOptions {
  maxWidth?: number;
  fontFamily?: string;
  primaryColor?: string;
  backgroundColor?: string;
}

const DEFAULT_AMP_OPTIONS: Required<AmpRenderOptions> = {
  maxWidth: 600,
  fontFamily: "Arial, Helvetica, sans-serif",
  primaryColor: "#1040CC",
  backgroundColor: "#ffffff",
};

/**
 * Render an array of AMP components to AMP4Email HTML.
 * Returns a complete AMP HTML document.
 */
export function renderAmpEmailHtml(
  components: AmpEmailComponent[],
  options: AmpRenderOptions = {},
): string {
  const opts = { ...DEFAULT_AMP_OPTIONS, ...options };

  // Collect required AMP scripts
  const scripts = new Set<string>();
  for (const c of components) {
    const config = AMP_COMPONENT_REGISTRY.find((r) => r.type === c.type);
    if (config) scripts.add(config.ampScript);
  }

  const scriptTags = [...scripts].map((src) =>
    `<script async custom-element="${extractElementName(src)}" src="${escapeAttr(src)}"></script>`
  ).join("\n");

  const bodyContent = components.map((c) => renderAmpComponent(c, opts)).join("\n");

  return `<!doctype html>
<html ⚡4email data-css-strict>
<head>
<meta charset="utf-8">
<script async src="https://cdn.ampproject.org/v0.js"></script>
${scriptTags}
<style amp4email>
  body { margin: 0; padding: 0; font-family: ${opts.fontFamily}; }
  .amp-container { max-width: ${opts.maxWidth}px; margin: 0 auto; padding: 20px; }
  .amp-section { margin-bottom: 16px; }
  .amp-btn { display: inline-block; padding: 12px 24px; background: ${opts.primaryColor}; color: #fff; border-radius: 6px; text-decoration: none; font-weight: 600; border: none; cursor: pointer; font-size: 16px; }
  .amp-btn:hover { opacity: 0.9; }
</style>
</head>
<body>
<div class="amp-container">
${bodyContent}
</div>
</body>
</html>`;
}

function renderAmpComponent(component: AmpEmailComponent, opts: Required<AmpRenderOptions>): string {
  const p = component.props;

  switch (component.type) {
    case "amp_booking": {
      const buttonText = String(p.buttonText ?? "Book Now");
      return `<div class="amp-section">
<amp-list src="{{booking_slots_url}}" layout="fixed-height" height="200" items=".">
  <template type="amp-mustache">
    <div style="padding:8px 0;border-bottom:1px solid #eee;">
      <span style="font-weight:600;">{{time}}</span> — {{provider}}
      <a href="{{book_url}}" class="amp-btn" style="float:right;padding:6px 16px;font-size:14px;">${escapeHtml(buttonText)}</a>
    </div>
  </template>
</amp-list>
</div>`;
    }

    case "amp_poll": {
      const question = String(p.question ?? "");
      const options = Array.isArray(p.options) ? p.options as string[] : [];
      const optionButtons = options.map((opt, i) =>
        `<button type="submit" name="choice" value="${i}" class="amp-btn" style="margin:4px;padding:8px 20px;">${escapeHtml(opt)}</button>`
      ).join("\n");
      return `<div class="amp-section">
<p style="font-weight:600;font-size:18px;margin-bottom:12px;">${escapeHtml(question)}</p>
<form method="post" action-xhr="{{poll_submit_url}}" target="_top">
  ${optionButtons}
  <div submit-success><template type="amp-mustache"><p style="color:${opts.primaryColor};font-weight:600;">Thanks for voting!</p></template></div>
</form>
</div>`;
    }

    case "amp_carousel": {
      const images = Array.isArray(p.images) ? p.images as string[] : [];
      const slides = images.map((src) =>
        `<amp-img src="${escapeAttr(src)}" width="560" height="315" layout="responsive" alt=""></amp-img>`
      ).join("\n");
      return `<div class="amp-section">
<amp-carousel width="560" height="315" layout="responsive" type="slides" autoplay delay="${String(p.delay ?? 3000)}">
  ${slides}
</amp-carousel>
</div>`;
    }

    case "amp_accordion": {
      const sections = Array.isArray(p.sections) ? p.sections as Array<{ title: string; content: string }> : [];
      const expandFirst = p.expandFirst !== false;
      const items = sections.map((s, i) =>
        `<section${i === 0 && expandFirst ? " expanded" : ""}>
  <h3 style="padding:12px 16px;margin:0;background:#f5f5f5;border-bottom:1px solid #eee;cursor:pointer;">${escapeHtml(s.title)}</h3>
  <div style="padding:12px 16px;">${escapeHtml(s.content)}</div>
</section>`
      ).join("\n");
      return `<div class="amp-section">
<amp-accordion animate>
  ${items}
</amp-accordion>
</div>`;
    }

    case "amp_nps": {
      const question = String(p.question ?? "How likely are you to recommend us?");
      const buttons = Array.from({ length: 10 }, (_, i) => i + 1).map((n) =>
        `<button type="submit" name="score" value="${n}" style="width:36px;height:36px;margin:2px;border:1px solid #ddd;border-radius:4px;background:${n >= 9 ? "#16a34a" : n >= 7 ? opts.primaryColor : n >= 5 ? "#d97706" : "#dc2626"};color:#fff;font-weight:600;cursor:pointer;border:none;">${n}</button>`
      ).join("");
      return `<div class="amp-section">
<p style="font-weight:600;font-size:16px;margin-bottom:8px;">${escapeHtml(question)}</p>
<form method="post" action-xhr="{{nps_submit_url}}" target="_top">
  <div style="display:flex;gap:2px;flex-wrap:wrap;">${buttons}</div>
  <div style="display:flex;justify-content:space-between;font-size:12px;color:#999;margin-top:4px;">
    <span>Not likely</span><span>Very likely</span>
  </div>
  <div submit-success><template type="amp-mustache"><p style="color:${opts.primaryColor};font-weight:600;margin-top:8px;">Thank you for your feedback!</p></template></div>
</form>
</div>`;
    }

    case "amp_preferences": {
      const categories = Array.isArray(p.categories) ? p.categories as string[] : [];
      const checkboxes = categories.map((cat) =>
        `<label style="display:block;padding:8px 0;border-bottom:1px solid #f0f0f0;">
  <input type="checkbox" name="prefs" value="${escapeAttr(cat)}" checked> ${escapeHtml(cat)}
</label>`
      ).join("\n");
      return `<div class="amp-section">
<p style="font-weight:600;font-size:16px;margin-bottom:8px;">Your Preferences</p>
<form method="post" action-xhr="{{preferences_submit_url}}" target="_top">
  ${checkboxes}
  <button type="submit" class="amp-btn" style="margin-top:12px;">Save Preferences</button>
  <div submit-success><template type="amp-mustache"><p style="color:${opts.primaryColor};font-weight:600;">Preferences saved!</p></template></div>
</form>
</div>`;
    }

    default:
      return "";
  }
}

function extractElementName(scriptUrl: string): string {
  const match = scriptUrl.match(/\/v0\/(amp-[a-z-]+)/);
  return match?.[1] ?? "amp-unknown";
}

function escapeHtml(text: string): string {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

function escapeAttr(text: string): string {
  return text.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/**
 * Generate standard HTML fallback for an AMP component.
 * Used when the email client doesn't support AMP4Email.
 */
export function renderAmpFallbackHtml(component: AmpEmailComponent): string {
  const p = component.props;

  switch (component.type) {
    case "amp_booking":
      return `<tr><td style="padding:15px 30px;text-align:center;">
<p style="font-size:16px;font-weight:600;">Book Your Appointment</p>
<a href="{{booking_url}}" style="display:inline-block;padding:12px 24px;background:#1040CC;color:#fff;border-radius:6px;text-decoration:none;font-weight:600;">${escapeHtml(String(p.buttonText ?? "Book Now"))}</a>
</td></tr>`;

    case "amp_poll":
      return `<tr><td style="padding:15px 30px;">
<p style="font-size:16px;font-weight:600;">${escapeHtml(String(p.question ?? ""))}</p>
<p style="color:#666;">Vote in your browser: <a href="{{poll_url}}" style="color:#1040CC;">Take the poll →</a></p>
</td></tr>`;

    case "amp_carousel": {
      const images = Array.isArray(p.images) ? p.images as string[] : [];
      const firstImage = images[0];
      return firstImage
        ? `<tr><td style="padding:15px 30px;text-align:center;"><img src="${escapeAttr(firstImage)}" width="540" style="max-width:100%;height:auto;" alt=""></td></tr>`
        : "";
    }

    case "amp_accordion": {
      const sections = Array.isArray(p.sections) ? p.sections as Array<{ title: string; content: string }> : [];
      return sections.map((s) =>
        `<tr><td style="padding:10px 30px;"><p style="font-weight:600;margin:0 0 4px;">${escapeHtml(s.title)}</p><p style="margin:0;color:#666;">${escapeHtml(s.content)}</p></td></tr>`
      ).join("\n");
    }

    case "amp_nps":
      return `<tr><td style="padding:15px 30px;">
<p style="font-size:16px;font-weight:600;">${escapeHtml(String(p.question ?? ""))}</p>
<p style="color:#666;">Rate us: <a href="{{nps_url}}" style="color:#1040CC;">Give feedback →</a></p>
</td></tr>`;

    case "amp_preferences":
      return `<tr><td style="padding:15px 30px;">
<p style="font-size:16px;font-weight:600;">Manage Your Preferences</p>
<a href="{{preferences_url}}" style="display:inline-block;padding:10px 20px;background:#1040CC;color:#fff;border-radius:6px;text-decoration:none;">Update Preferences →</a>
</td></tr>`;

    default:
      return "";
  }
}
